/**
 * Extra diagnosis IDs loaded in Pass 1 when patient context is high-impact.
 * IDs must exist in KB seed (phase1KbSeedData).
 */

import {
  IMMUNOSUPPRESSION_STATUS,
  PREGNANCY_STATUS,
  DRUG_USE,
  RELEVANT_COMORBIDITIES,
} from './ddxTemporalTaxonomy.js';

/** Diagnoses always merged into can't-miss when pregnant or postpartum (KB ids). */
export const PREGNANCY_CANT_MISS_DX_IDS = Object.freeze(['dx_ectopic', 'dx_pe']);

const PREGNANT_OR_POSTPARTUM = new Set([
  PREGNANCY_STATUS.PREGNANT_FIRST,
  PREGNANCY_STATUS.PREGNANT_SECOND,
  PREGNANCY_STATUS.PREGNANT_THIRD,
  PREGNANCY_STATUS.POSTPARTUM,
]);

/**
 * @param {import('./ddxTypes.js').PatientContextProfile | null | undefined} pc
 * @returns {boolean}
 */
export function isPregnantOrPostpartumContext(pc) {
  const s = pc?.pregnancyStatus;
  return Boolean(s && PREGNANT_OR_POSTPARTUM.has(s));
}

/** Opportunistic / severe infection axis when immune status is impaired. */
const IMMUNO_EXTRA = Object.freeze(['dx_pneumonia', 'dx_sepsis', 'dx_meningitis', 'dx_endocarditis']);

/** IVDU / cocaine stim infectious complications present in seed. */
const IVDU_EXTRA = Object.freeze(['dx_endocarditis', 'dx_spinal_epidural', 'dx_sepsis']);

/**
 * @param {import('./ddxTypes.js').PatientContextProfile | null | undefined} pc
 * @returns {string[]}
 */
export function getContextExpansionDxIds(pc) {
  if (!pc || typeof pc !== 'object') return [];
  const out = new Set();
  const flagged = Array.isArray(pc.flaggedHighImpactFactors) ? pc.flaggedHighImpactFactors : [];

  const imm = pc.immunosuppressionStatus;
  if (
    flagged.includes('immunosuppressionStatus') ||
    (imm && imm !== IMMUNOSUPPRESSION_STATUS.NONE && imm !== IMMUNOSUPPRESSION_STATUS.UNKNOWN)
  ) {
    for (const id of IMMUNO_EXTRA) out.add(id);
  }

  const drugs = Array.isArray(pc.drugUse) ? pc.drugUse : [];
  if (
    flagged.includes('drugUse') ||
    drugs.includes(DRUG_USE.IVDU) ||
    drugs.includes(DRUG_USE.COCAINE)
  ) {
    for (const id of IVDU_EXTRA) out.add(id);
  }

  const comorb = Array.isArray(pc.relevantComorbidities) ? pc.relevantComorbidities : [];
  if (comorb.includes(RELEVANT_COMORBIDITIES.MALIGNANCY) || comorb.includes(RELEVANT_COMORBIDITIES.PRIOR_PE_DVT)) {
    for (const id of IMMUNO_EXTRA) out.add(id);
    out.add('dx_pe');
  }

  return [...out];
}

/**
 * Prominent, human-readable alerts for UI (non-dismissible tier).
 * @param {import('./ddxTypes.js').PatientContextProfile | null | undefined} pc
 * @returns {string[]}
 */
export function buildHighImpactContextAlertStrings(pc) {
  const factors = Array.isArray(pc?.flaggedHighImpactFactors) ? pc.flaggedHighImpactFactors : [];
  if (factors.length === 0) return [];
  /** @type {string[]} */
  const out = [];
  if (factors.includes('immunosuppressionStatus')) {
    out.push('Immunosuppression: consider opportunistic infections; rankings may not match immunocompetent baselines.');
  }
  if (factors.includes('pregnancyStatus')) {
    out.push('Pregnancy or postpartum: obstetric emergencies must stay on the differential.');
  }
  if (factors.includes('drugUse')) {
    out.push('IV drug use or stimulant use: prioritize endocarditis, spinal infection, and systemic infection.');
  }
  if (factors.includes('relevantComorbidities')) {
    out.push('High-risk comorbidity (malignancy or prior clot): thromboembolic and complication-related diagnoses elevated.');
  }
  if (factors.includes('anticoagulationStatus')) {
    out.push('Anticoagulation or antiplatelet therapy: bleeding sources and reversal considerations may dominate.');
  }
  if (factors.includes('travelHistory')) {
    out.push('Recent travel outside usual endemic assumptions: expand geographic and tropical differentials.');
  }
  if (factors.includes('housingStatus')) {
    out.push('Housing instability or incarceration: TB, skin/soft tissue, and violence-related differentials elevated.');
  }
  return [...new Set(out)];
}
